import { useState, useEffect, useCallback, useRef } from 'react';
import { customerDuplicatesApi } from '../services/customerDuplicatesApi';
import type { PairRow, PairListSummary, PairFilters, PairSortField } from '../types/customerDuplicates';
import { EMPTY_FILTERS } from '../types/customerDuplicates';

/**
 * State for the /customer-duplicates work queue.
 *
 * Sorting, filtering and paging are all server-side (same contract as useDocuWarePoChecks), so
 * every setter resets the page. The default filter is `status=OPEN` — decided cases disappear
 * from the work queue until the "Alle" / a specific status is picked, per the masterplan
 * ("Entschiedene Fälle verschwinden und kommen ... NICHT zurück").
 */

export const PAGE_SIZE = 50;

const EMPTY_SUMMARY: PairListSummary = {
    byReason: {
        BLOCKED_REPLACEMENT: { total: 0, bothDormant: 0, oneSideDormant: 0 },
        MIGRATION: { total: 0, bothDormant: 0, oneSideDormant: 0 },
        VAT: { total: 0, bothDormant: 0, oneSideDormant: 0 },
        BUSINESS_UNIT: { total: 0, bothDormant: 0, oneSideDormant: 0 },
        SITE: { total: 0, bothDormant: 0, oneSideDormant: 0 },
        UNCLEAR: { total: 0, bothDormant: 0, oneSideDormant: 0 },
    },
    byCompany: {}, byLevel: {}, byStatus: {},
    affectedAccounts: 0, openItems: 0, openAmountEur: 0, accountsWithOpenItems: 0,
    revenueEur: 0, revenueWindowMonths: 24, accountsWithRevenue: 0,
    latestRun: null,
    registryCoverage: { checked: 0, total: 0 },
    goneNotDecided: 0,
};

export function useCustomerDuplicatePairs(enabled: boolean) {
    const [rows, setRows] = useState<PairRow[]>([]);
    const [summary, setSummary] = useState<PairListSummary>(EMPTY_SUMMARY);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const [filters, setFiltersState] = useState<PairFilters>(EMPTY_FILTERS);
    const [sortBy, setSortBy] = useState<PairSortField>('score');
    const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
    const [page, setPage] = useState(1);

    const requestSeq = useRef(0);

    const load = useCallback(async () => {
        if (!enabled) return;
        const seq = ++requestSeq.current;
        setLoading(true);
        setError(null);
        try {
            const result = await customerDuplicatesApi.list({
                page, limit: PAGE_SIZE, filters, sortBy, sortOrder,
            });
            if (seq !== requestSeq.current) return;
            setRows(result.items);
            setSummary(result.summary);
            setTotal(result.total);
        } catch (err) {
            if (seq !== requestSeq.current) return;
            setError(err instanceof Error ? err.message : 'Failed to load customer duplicates');
            setRows([]);
            setTotal(0);
        } finally {
            if (seq === requestSeq.current) setLoading(false);
        }
    }, [enabled, page, filters, sortBy, sortOrder]);

    useEffect(() => { void load(); }, [load]);

    const setFilters = useCallback((next: Partial<PairFilters>) => {
        setFiltersState(prev => ({ ...prev, ...next }));
        setPage(1);
    }, []);

    const clearFilters = useCallback(() => {
        setFiltersState(EMPTY_FILTERS);
        setPage(1);
    }, []);

    const setSort = useCallback((field: PairSortField) => {
        setSortBy(prev => {
            if (prev === field) {
                setSortOrder(o => (o === 'asc' ? 'desc' : 'asc'));
                return prev;
            }
            setSortOrder('desc');
            return field;
        });
        setPage(1);
    }, []);

    // `status` and `presence` both have a non-empty DEFAULT, so "set" is not the same as "active"
    // for them - counting them always would show "Clear filters (2)" on an untouched page.
    const activeFilterCount = Object.entries(filters)
        .filter(([k, v]) => {
            if (k === 'status') return v !== 'OPEN' && v !== '';
            if (k === 'presence') return v !== 'CURRENT' && v !== '';
            return v !== '' && v !== false;
        })
        .length;
    const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

    return {
        rows, summary, total, loading, error,
        filters, setFilters, clearFilters, activeFilterCount,
        sortBy, sortOrder, setSort,
        page, setPage, totalPages,
        reload: load,
    };
}
